import { useState, type ReactNode } from "react"
import { useNavigate } from "@tanstack/react-router"
import { ShieldAlert } from "lucide-react"

import { Button } from "@/components/ui/button"
import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"
import { ApiRequestError } from "@/lib/api"
import { logout } from "@/lib/auth/auth-store"

type PortalAccessGateProps = {
	readonly children: ReactNode
}

type AuthErrorCopy = {
	readonly title: string
	readonly message: string
}

function getAuthErrorCopy(error: unknown): AuthErrorCopy {
	if (error instanceof ApiRequestError) {
		if (error.status === 401) {
			return {
				title: "Your session has expired",
				message: "Sign out and sign in again to continue.",
			}
		}

		if (error.status === 403 && error.code === "user_disabled") {
			return {
				title: "This account is disabled",
				message: "Contact a Super Admin to restore portal access.",
			}
		}
	}

	return {
		title: "Portal access could not be checked",
		message: "Try again. If the problem continues, contact support.",
	}
}

function AccessStateCard(props: {
	readonly title: string
	readonly message: string
	readonly children: ReactNode
}) {
	return (
		<div className="flex min-h-[60vh] items-center justify-center">
			<section
				className="w-full max-w-xl rounded-2xl border border-border bg-card p-8 text-center shadow-card"
				role="alert"
				aria-labelledby="portal-access-title"
			>
				<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-brand-muted text-brand-foreground">
					<ShieldAlert className="size-6" aria-hidden="true" />
				</div>
				<h1
					id="portal-access-title"
					className="mt-5 font-avenir text-page-title font-semibold text-heading text-pretty"
				>
					{props.title}
				</h1>
				<p className="mx-auto mt-3 max-w-md font-inter text-body text-muted-foreground">
					{props.message}
				</p>
				<div className="mt-6 flex flex-wrap justify-center gap-3">{props.children}</div>
			</section>
		</div>
	)
}

export function PortalAccessGate({ children }: PortalAccessGateProps) {
	const navigate = useNavigate()
	const authQuery = usePortalAuth()
	const [isSigningOut, setIsSigningOut] = useState(false)
	const [signOutError, setSignOutError] = useState<string | null>(null)

	async function handleSignOut(): Promise<void> {
		setIsSigningOut(true)
		setSignOutError(null)

		try {
			await logout()
			await navigate({ to: "/" })
		} catch {
			setSignOutError("Unable to return to sign in. Refresh the page and try again.")
			setIsSigningOut(false)
		}
	}

	if (authQuery.isPending) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center" role="status" aria-live="polite">
				<p className="font-inter text-body text-muted-foreground">Checking portal access…</p>
			</div>
		)
	}

	if (authQuery.isError) {
		const copy = getAuthErrorCopy(authQuery.error)
		return (
			<AccessStateCard title={copy.title} message={copy.message}>
				<Button aria-busy={authQuery.isFetching}
					type="button"
					variant="outline"
					disabled={authQuery.isFetching || isSigningOut}
					onClick={() => {
						void authQuery.refetch()
					}}
				>
					{authQuery.isFetching ? "Checking…" : "Try again"}
				</Button>
				<Button aria-busy={isSigningOut} type="button" disabled={isSigningOut} onClick={() => void handleSignOut()}>
					{isSigningOut ? "Signing out…" : "Sign out"}
				</Button>
				{signOutError ? (
					<p className="basis-full font-inter text-meta text-destructive" role="alert">
						{signOutError}
					</p>
				) : null}
			</AccessStateCard>
		)
	}

	const auth = authQuery.data
	if (auth === undefined) {
		throw new Error("Portal auth query returned no data")
	}

	if (auth.roles.length === 0) {
		return (
			<AccessStateCard
				title="Portal access is not assigned"
				message="Your sign-in succeeded, but this account has no portal role. Ask a Super Admin to assign a role, then sign in again."
			>
				<Button aria-busy={isSigningOut} type="button" disabled={isSigningOut} onClick={() => void handleSignOut()}>
					{isSigningOut ? "Signing out…" : "Sign out"}
				</Button>
				{signOutError ? (
					<p className="basis-full font-inter text-meta text-destructive" role="alert">
						{signOutError}
					</p>
				) : null}
			</AccessStateCard>
		)
	}

	return <>{children}</>
}
